import { useCallback, useState } from 'react';
import ZoeListDownloadPreview from './ZoeListDownloadPreview';
import type {
  OpsBriefAnswerGroup,
  OpsBriefBlock,
  OpsBriefRow,
  OpsBriefSection,
  OpsBriefValuePart,
} from '../../types/investigator';
import { AlertTriangle, CheckCircle2, Clock, Info, MapPin, Radio } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LABEL =
  'text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5';

const TONE_ICON: Record<NonNullable<OpsBriefRow['tone']>, typeof Info> = {
  info: Info,
  warning: AlertTriangle,
  critical: AlertTriangle,
  positive: CheckCircle2,
};

const TONE_CLASS: Record<NonNullable<OpsBriefRow['tone']>, string> = {
  info: 'text-ring',
  warning: 'text-warning',
  critical: 'text-destructive',
  positive: 'text-success',
};

const ID_CLASS =
  'font-mono text-sm text-ring underline decoration-ring/40 underline-offset-2 hover:text-ring hover:decoration-ring cursor-pointer';

function RowIcon({ sectionId, row }: { sectionId: string; row: OpsBriefRow }) {
  if (row.tone) {
    const Icon = TONE_ICON[row.tone];
    return <Icon className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${TONE_CLASS[row.tone]}`} />;
  }
  // Fleet roster breakdowns are plain tables — no MapPin / activity icons.
  if (sectionId === 'fleet_body_type' || sectionId === 'fleet_payload') {
    return null;
  }
  if (sectionId === 'activity') {
    const l = row.label.toLowerCase();
    if (l.includes('active time')) return <Clock className="w-3.5 h-3.5 shrink-0 mt-0.5 text-muted-foreground" />;
    if (l.includes('gps')) return <Radio className="w-3.5 h-3.5 shrink-0 mt-0.5 text-muted-foreground" />;
    if (l.includes('movement')) return <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5 text-muted-foreground" />;
    return <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5 text-muted-foreground" />;
  }
  return null;
}

function valueToneClass(tone: OpsBriefRow['tone']): string {
  if (tone === 'warning') return 'text-warning';
  if (tone === 'critical') return 'text-destructive';
  if (tone === 'positive') return 'text-success';
  return 'text-foreground';
}

async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Ignore — clipboard may be unavailable in some embedded contexts.
  }
}

function IdentifierChip({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const onClick = useCallback(async () => {
    await copyText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  }, [value]);

  return (
    <Button
      type="button"
      onClick={onClick}
      title={copied ? 'Copied' : `Copy ${value}`}
      variant="link"
      className={`${ID_CLASS} inline h-auto p-0`}
    >
      {copied ? `${value} ✓` : value}
    </Button>
  );
}

function IdListPart({
  values,
  previewCount,
}: {
  values: string[];
  previewCount: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const safePreview = Math.max(0, previewCount);
  const visible = expanded || values.length <= safePreview
    ? values
    : values.slice(0, safePreview);
  const hiddenCount = Math.max(0, values.length - visible.length);

  return (
    <span className="inline">
      (
      {visible.map((id, i) => (
        <span key={`${id}-${i}`}>
          {i > 0 ? <span className="text-muted-foreground">, </span> : null}
          <IdentifierChip value={id} />
        </span>
      ))}
      {hiddenCount > 0 ? (
        <>
          <span className="text-muted-foreground">, </span>
          <Button
            type="button"
            onClick={() => setExpanded(true)}
            variant="link"
            className="inline h-auto p-0 text-sm text-ring/90 decoration-dotted underline-offset-2 hover:text-ring"
            title={`Show ${hiddenCount} more`}
          >
            +{hiddenCount} more
          </Button>
        </>
      ) : null}
      {expanded && values.length > safePreview ? (
        <>
          <span className="text-muted-foreground"> · </span>
          <Button
            type="button"
            onClick={() => setExpanded(false)}
            variant="ghost"
            className="inline h-auto p-0 text-xs hover:bg-transparent"
            title="Collapse"
          >
            show less
          </Button>
        </>
      ) : null}
      )
    </span>
  );
}

/** Vertical list of plain lines with clickable "+N more" expand. */
function LineListPart({
  lines,
  previewCount,
}: {
  lines: string[];
  previewCount: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const safePreview = Math.max(0, previewCount);
  const visible =
    expanded || lines.length <= safePreview ? lines : lines.slice(0, safePreview);
  const hiddenCount = Math.max(0, lines.length - visible.length);

  if (lines.length === 0) {
    return <span>None</span>;
  }

  return (
    <span className="inline-flex flex-col items-start gap-0.5 align-top">
      {visible.map((line, i) => (
        <span key={`${line}-${i}`} className="block">
          {line}
        </span>
      ))}
      {hiddenCount > 0 ? (
        <Button
          type="button"
          onClick={() => setExpanded(true)}
          variant="link"
          className="inline h-auto p-0 text-sm text-ring/90 decoration-dotted underline-offset-2 hover:text-ring mt-0.5"
          title={`Show ${hiddenCount} more`}
        >
          +{hiddenCount} more
        </Button>
      ) : null}
      {expanded && lines.length > safePreview ? (
        <Button
          type="button"
          onClick={() => setExpanded(false)}
          variant="ghost"
          className="inline h-auto p-0 text-xs hover:bg-transparent mt-0.5"
          title="Collapse"
        >
          show less
        </Button>
      ) : null}
    </span>
  );
}

function ValueParts({ parts, tone }: { parts: OpsBriefValuePart[]; tone?: OpsBriefRow['tone'] }) {
  return (
    <span className={valueToneClass(tone)}>
      {parts.map((part, i) => {
        if (part.type === 'text') {
          return <span key={i}>{part.text}</span>;
        }
        if (part.type === 'id') {
          return <IdentifierChip key={i} value={part.value} />;
        }
        if (part.type === 'line_list') {
          return (
            <LineListPart key={i} lines={part.lines} previewCount={part.preview_count} />
          );
        }
        return (
          <IdListPart
            key={i}
            values={part.values}
            previewCount={part.preview_count}
          />
        );
      })}
    </span>
  );
}

function SectionRows({ section }: { section: OpsBriefSection }) {
  return (
    <ul className="mt-1 space-y-2">
      {section.rows.map((row, i) => (
        <li key={`${row.label}-${i}`} className="flex items-start gap-2 text-sm">
          <RowIcon sectionId={section.id} row={row} />
          <div className="min-w-0 leading-snug">
            <span className="text-muted-foreground">{row.label}: </span>
            {row.value_parts && row.value_parts.length > 0 ? (
              <ValueParts parts={row.value_parts} tone={row.tone} />
            ) : (
              <span className={valueToneClass(row.tone)}>{row.value}</span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function SectionCard({
  section,
  delayMs,
}: {
  section: OpsBriefSection;
  delayMs: number;
}) {
  return (
    <div
      className="bg-background/30 border border-border/40 rounded-lg p-3 zoe-snap-in"
      style={{ animationDelay: `${delayMs}ms` }}
    >
      <div className={LABEL}>{section.title}</div>
      <SectionRows section={section} />
    </div>
  );
}

/**
 * Walk sections in order. When body-type and payload sit next to each other,
 * render them as a side-by-side pair (stack on narrow screens).
 */
function groupSectionsForLayout(sections: OpsBriefSection[]): Array<
  | { kind: 'single'; section: OpsBriefSection }
  | { kind: 'pair'; left: OpsBriefSection; right: OpsBriefSection }
> {
  const groups: Array<
    | { kind: 'single'; section: OpsBriefSection }
    | { kind: 'pair'; left: OpsBriefSection; right: OpsBriefSection }
  > = [];

  for (let i = 0; i < sections.length; i++) {
    const current = sections[i]!;
    const next = sections[i + 1];
    if (
      current.id === 'fleet_body_type' &&
      next?.id === 'fleet_payload'
    ) {
      groups.push({ kind: 'pair', left: current, right: next });
      i += 1;
      continue;
    }
    groups.push({ kind: 'single', section: current });
  }

  return groups;
}

/**
 * T115 — the consignments a list-shaped question asked for, one per line: id,
 * what is wrong, whose it is, and where it is underneath.
 */
function AnswerGroups({ groups, downloadable = false }: { groups: OpsBriefAnswerGroup[]; downloadable?: boolean }) {
  if (groups.length === 0) return null;
  return (
    <div className="space-y-3">
      {groups.map((group) => {
        const unlisted = group.count - group.rows.length;
        return (
          <div key={group.title} className="bg-gray-900/30 border border-gray-700/40 rounded-lg p-3 zoe-snap-in">
            <div className="text-sm font-semibold text-gray-100">
              {group.title} <span className="font-normal text-gray-400">({group.count})</span>
            </div>
            <ul className="mt-2 space-y-2">
              {group.rows.map((r, i) => (
                // The customer sits right of the order number, where it is seen
                // first (CEO, 21 Oct 2026); what is wrong goes underneath.
                <li key={`${r.id}-${i}`} className="grid grid-cols-auto-1fr gap-x-3 text-sm leading-snug">
                  <IdentifierChip value={r.id} />
                  <div className="min-w-0">
                    {r.who ? <div className="text-gray-100 font-medium">{r.who}</div> : null}
                    <div className={r.who ? 'text-gray-300' : 'text-gray-100'}>{r.what}</div>
                    {r.detail ? <div className="text-xs text-gray-500 mt-0.5">{r.detail}</div> : null}
                  </div>
                </li>
              ))}
            </ul>
            {unlisted > 0 ? (
              <p className="text-xs text-gray-500 mt-2">
                {unlisted} more not listed: {downloadable ? 'the full list is in the download above.' : 'ask Zoe for them.'}
              </p>
            ) : null}
            {group.note ? <p className="text-xs text-gray-500 mt-2">{group.note}</p> : null}
          </div>
        );
      })}
    </div>
  );
}

/** T115 — notes that change what the answer means: small, and in view under it. */
function NoteRows({ rows }: { rows: OpsBriefRow[] }) {
  return (
    <ul className="space-y-1 zoe-snap-in">
      {rows.map((row, i) => {
        const tone = row.tone ?? 'info';
        const Icon = TONE_ICON[tone];
        return (
          <li key={`${row.label}-${i}`} className="flex items-start gap-2 text-xs leading-snug">
            <Icon className={`w-3.5 h-3.5 shrink-0 mt-px ${TONE_CLASS[tone]}`} />
            <span className={tone === 'warning' || tone === 'critical' ? 'text-amber-100/90' : 'text-gray-400'}>
              <span className="font-medium">{row.label}:</span> {row.value}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

interface OpsBriefPanelProps {
  block: OpsBriefBlock;
  staggerMs?: number;
  /** When the summary line is already shown as the card headline, omit the SUMMARY panel. */
  hideSummarySection?: boolean;
  /**
   * T115 — which part of the card to draw: under the answer (the list asked
   * for, sections that are part of the answer, and notes that change its
   * meaning), the closed "More detail" (summary strip, what is not asked
   * about), or everything (the default, as the Investigator tab shows it).
   */
  part?: 'all' | 'answer' | 'detail';
}

export default function OpsBriefPanel({
  block,
  staggerMs = 80,
  hideSummarySection = false,
  part = 'all',
}: OpsBriefPanelProps) {
  const answerFirst = block.answer_first;
  if (part === 'answer') {
    const inView = block.sections.filter((s) => !s.detail && s.id !== 'data_quality');
    const notes = block.sections.filter((s) => !s.detail && s.id === 'data_quality').flatMap((s) => s.rows);
    return (
      <div className="space-y-3">
        {block.download ? <ZoeListDownloadPreview download={block.download} /> : null}
        {answerFirst ? <AnswerGroups groups={answerFirst.groups} downloadable={Boolean(block.download)} /> : null}
        {groupSectionsForLayout(inView).map((group, idx) =>
          group.kind === 'pair' ? (
            <div
              key={`${group.left.id}-${group.right.id}`}
              className="grid grid-cols-1 sm:grid-cols-2 gap-3 zoe-snap-in"
              style={{ animationDelay: `${idx * staggerMs}ms` }}
            >
              <SectionCard section={group.left} delayMs={0} />
              <SectionCard section={group.right} delayMs={0} />
            </div>
          ) : (
            <SectionCard key={group.section.id} section={group.section} delayMs={idx * staggerMs} />
          ),
        )}
        {notes.length > 0 ? <NoteRows rows={notes} /> : null}
      </div>
    );
  }
  if (part === 'detail') {
    const detailSections = block.sections.filter((s) => s.detail);
    return (
      <div className="space-y-3">
        {block.summary_line && !hideSummarySection ? (
          <div className="bg-gray-900/30 border border-gray-700/40 rounded-lg p-3">
            <div className={LABEL}>Summary</div>
            <p className="text-sm text-gray-100 leading-snug whitespace-pre-line">{block.summary_line}</p>
          </div>
        ) : null}
        {answerFirst ? <AnswerGroups groups={answerFirst.more_groups} downloadable={Boolean(block.download)} /> : null}
        {detailSections.map((section, i) => (
          <SectionCard key={section.id} section={section} delayMs={i * staggerMs} />
        ))}
      </div>
    );
  }

  let sectionIndex = 0;
  const showSummary = block.summary_line && !hideSummarySection;
  const layoutGroups = groupSectionsForLayout(block.sections);

  return (
    <div className="space-y-3">
      {block.download ? <ZoeListDownloadPreview download={block.download} /> : null}
      {answerFirst ? (
        <AnswerGroups groups={[...answerFirst.groups, ...answerFirst.more_groups]} downloadable={Boolean(block.download)} />
      ) : null}
      {showSummary ? (
        <div
          className="bg-background/30 border border-border/40 rounded-lg p-3 zoe-snap-in"
          style={{ animationDelay: `${sectionIndex++ * staggerMs}ms` }}
        >
          <div className={LABEL}>Summary</div>
          <p className="text-sm text-foreground leading-snug font-medium whitespace-pre-line">{block.summary_line}</p>
        </div>
      ) : null}

      {layoutGroups.map((group) => {
        const idx = sectionIndex++;
        if (group.kind === 'pair') {
          return (
            <div
              key={`${group.left.id}-${group.right.id}`}
              className="grid grid-cols-1 sm:grid-cols-2 gap-3 zoe-snap-in"
              style={{ animationDelay: `${idx * staggerMs}ms` }}
            >
              <SectionCard section={group.left} delayMs={0} />
              <SectionCard section={group.right} delayMs={0} />
            </div>
          );
        }
        return (
          <SectionCard
            key={group.section.id}
            section={group.section}
            delayMs={idx * staggerMs}
          />
        );
      })}
    </div>
  );
}
